// Work with ALL data from a collection.
// Specifically for search/drop down inputs.
// All other areas should use pagination.

import { useQuery } from "@tanstack/react-query";
import { useDeepCompareMemoize } from "use-deep-compare-effect";

import useCurrentUser from "~/hooks/user/useCurrentUser";
import createKey from "~/lib/cache/createKey";
import { getAllCollectionItems } from "~/lib/payload/collection";
import type { CollectionNames } from "~/types";
import type { Where } from "~/util/payload";

/**
 * Interface for the parameters of the getAllData function.
 */
interface GetAllDataParams {
  collection: CollectionNames;
  sort?: string;
  where?: Where;
  depth?: number;
}

/**
 * Interface for the parameters of the useAllData function.
 */
interface UseAllDataParams extends GetAllDataParams {
  keyAddition?: string;
  serialLoading?: boolean;
}

/**
 * Interface for the return type of the useAllData function.
 */
interface UseAllDataReturn<T> {
  data: T[] | undefined;
  totalItems: number;
  isLoading: boolean;
  isUpdating: boolean;
  refetch: () => void;
}

/**
 * Custom hook for fetching all data from a collection.
 * @param {UseAllDataParams} params - The parameters for the hook.
 * @returns {UseAllDataReturn<T>} - The data, loading state, and refetch function.
 */
export default function useAllData<T = any>({
  collection,
  sort,
  where,
  depth,
  keyAddition,
  serialLoading,
}: UseAllDataParams): UseAllDataReturn<T> {
  const {
    data: { id: userId },
    isLoading: userLoading,
  } = useCurrentUser();

  const whereCondition = useDeepCompareMemoize(where);

  const key = createKey({
    userId,
    collection,
    depth,
    keyAddition,
    all: true,
  });

  const runQuery = Boolean(!serialLoading && !userLoading && collection);

  const { data, isLoading, isFetching, refetch } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [key, whereCondition, sort],
    queryFn: () =>
      getAllCollectionItems<T>({
        collection,
        sort,
        where: whereCondition,
        depth,
      }),
    enabled: runQuery,
    refetchOnWindowFocus: false,
  });

  const refetchQuery = () => {
    refetch();
  };

  return {
    data: data && "data" in data ? (data.data as T[]) : undefined,
    totalItems: data && "totalItems" in data ? data.totalItems : 0,
    isLoading: isLoading || userLoading,
    isUpdating: isLoading || userLoading || isFetching,
    refetch: refetchQuery,
  };
}
